// 플레이어(Knight) 클래스 - Party 상속
class Player extends Party {
  constructor(scene) {
    // PlayerConfig를 기반으로 설정 구성
    const config = {
      MAX_HEALTH: PlayerConfig.MAX_HEALTH,
      MOVE_SPEED: PlayerConfig.MOVE_SPEED,
      COLLIDER_RADIUS: PlayerConfig.COLLIDER_RADIUS,
      ATTACK_RANGE: PlayerConfig.ATTACK_RANGE,
      ATTACK_ANGLE: PlayerConfig.ATTACK_ANGLE,
      ATTACK_SECTORS: PlayerConfig.ATTACK_SECTORS,
      SHOW_ATTACK_VISUAL: PlayerConfig.SHOW_ATTACK_VISUAL,
      ATTACK_DAMAGE: PlayerConfig.ATTACK_DAMAGE,
      ATTACK_COOLTIME: PlayerConfig.ATTACK_COOLTIME,
      HIT_STUN_DURATION: PlayerConfig.HIT_STUN_DURATION,
      HIT_FLASH_DURATION: PlayerConfig.HIT_FLASH_DURATION,
      DEATH_ANIMATION_DURATION: PlayerConfig.DEATH_ANIMATION_DURATION,
      MODEL_PATH: PlayerConfig.MODEL_PATH,
      SCALE: 1.0,
    };

    // 시작 위치 설정
    const startPos = VectorUtils.objectToVector3(
      PlayerConfig.STARTING_POSITION
    );
    super(scene, startPos.x, startPos.y, startPos.z, config);

    // Player 전용 속성: 추가 죽음 애니메이션 (앞으로 넘어지기)
    this.deathAction2 = null; // 25번 죽음 애니메이션 (앞으로 넘어지기)

    // 포인트 시스템
    this.points = PlayerConfig.STARTING_POINTS;

    // 레벨 시스템 - Point를 경험치로 사용
    this.level = GameConfig.LEVEL_SYSTEM.STARTING_LEVEL;
    this.pointsToNextLevel = this.calculatePointsToNextLevel();
    this.isLevelUpPending = false; // 레벨업 대기 상태 (레벨바가 끝에 닿을 때까지 대기)
    this.needsLevelUpRecheck = false; // 업그레이드 창이 열려있을 때 경험치를 쌓은 경우 재체크 필요
    // TODO : 이건 다른 매니저에서 관리하는게 낫지 않나? 왜냐면 Companion도 죽일 수 있자나
    this.killedSkeletons = 0; // 죽인 해골 수 카운터

    // 수집 시스템
    this.collectionRadius = PlayerConfig.COLLECTION_RADIUS; // 다이아몬드 수집 반경
  }

  // 플레이어 초기화 (모델 로드) - 부모 클래스 메서드 오버라이드
  async init() {
    return new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        this.config.MODEL_PATH,
        (gltf) => {
          this.model = gltf.scene;

          // 모델 속성 설정 (부모 클래스 메서드 호출)
          this.setupModelProperties();

          // 애니메이션 설정 (플레이어 전용)
          this.setupAnimations(gltf.animations);

          // 초기 검/방패 설정 (레벨 0 = 무기 없음)
          this.updateSword(0);
          this.updateShield(0);

          // 씬에 추가
          this.scene.add(this.model);

          // 체력바 생성
          this.createHealthBar();

          // 부채꼴 공격 범위 시각화 생성 (항상 표시)
          this.createAttackVisual();

          this.isLoaded = true;
          resolve();
        },
        (progress) => {
          Utils.log(
            "로딩 진행률:",
            (progress.loaded / progress.total) * 100 + "%"
          );
        },
        (error) => {
          console.error("Knight 모델 로드 오류:", error);
          reject(error);
        }
      );
    });
  }

  // 모델 속성 설정 (그림자, 무기 제거 등) - 부모 클래스 메서드 오버라이드
  setupModelProperties() {
    // 부모 클래스 메서드 호출
    super.setupModelProperties();

    // 플레이어 전용 설정: 모든 무기와 방패 숨기기
    this.model.traverse((child) => {
      if (child.isMesh) {
        // 모든 검과 방패 숨기기
        if (child.name && child.name.toLowerCase().includes("sword")) {
          child.visible = false;
        }
        if (child.name && child.name.toLowerCase().includes("shield")) {
          child.visible = false;
        }
      }
    });
  }

  // 무기 변경 메서드 - 검 레벨에 따라 다른 검 활성화
  updateSword(swordLevel) {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh) {
        const childName = child.name ? child.name.toLowerCase() : "";

        // 모든 검 숨기기
        if (childName.includes("sword")) {
          child.visible = false;
        }

        // 레벨에 따라 적절한 검 활성화
        if (swordLevel >= 1 && swordLevel <= 2) {
          // 1-2레벨: 1H_Sword 활성화
          if (childName === "1h_sword") {
            child.visible = true;
          }
        } else if (swordLevel >= 3 && swordLevel <= 4) {
          // 3-4레벨: 2H_Sword 활성화
          if (childName === "2h_sword") {
            child.visible = true;
          }
        }
      }
    });

    // 검 레벨에 따른 공격 애니메이션 변경
    this.updateAttackAnimation(swordLevel);
  }

  // 방패 변경 메서드 - 방패 레벨에 따라 다른 방패 활성화
  updateShield(shieldLevel) {
    if (!this.model) return;

    this.model.traverse((child) => {
      if (child.isMesh) {
        const childName = child.name ? child.name.toLowerCase() : "";

        // 모든 방패 숨기기
        if (childName.includes("shield")) {
          child.visible = false;
        }

        // 레벨에 따라 적절한 방패 활성화
        if (shieldLevel >= 1 && shieldLevel <= 2) {
          // 1-2레벨: Badge_Shield 활성화
          if (childName === "badge_shield") {
            child.visible = true;
          }
        } else if (shieldLevel >= 3 && shieldLevel <= 4) {
          // 3-4레벨: Spike_Shield 활성화
          if (childName === "spike_shield") {
            child.visible = true;
          }
        }
      }
    });
  }

  // 공격 애니메이션 변경 메서드 - 검 레벨에 따라 다른 공격 애니메이션 사용
  updateAttackAnimation(swordLevel) {
    if (!this.mixer || !this.animations) return;

    // 검 레벨에 따른 공격 애니메이션 선택
    let attackAnimationIndex;
    if (swordLevel >= 1) {
      // 검을 쥐게 되면 공격 애니메이션을 1번으로 변경
      attackAnimationIndex = 1;
    } else {
      // 검이 없으면 기본 공격 애니메이션 (68번)
      attackAnimationIndex = 68;
    }

    // 한국어 주석: 선택된 공격 애니메이션이 유효한지(트랙 존재) 검증 후 적용, 아니면 기본값으로 폴백
    const desiredClip = this.animations[attackAnimationIndex];
    const isDesiredValid = !!(
      desiredClip &&
      desiredClip.tracks &&
      desiredClip.tracks.length > 0
    );

    // 폴백 인덱스(기본 공격)
    const fallbackIndex = 68;
    const fallbackClip = this.animations[fallbackIndex];
    const isFallbackValid = !!(
      fallbackClip &&
      fallbackClip.tracks &&
      fallbackClip.tracks.length > 0
    );

    const finalClip = isDesiredValid
      ? desiredClip
      : isFallbackValid
      ? fallbackClip
      : null;

    if (!finalClip) {
      // 한국어 주석: 어떤 이유로 선택/폴백 클립 모두 유효하지 않으면 기존 attackAction 유지
      console.warn(
        `[Player] 유효한 공격 애니메이션을 찾지 못했습니다. 현재 설정을 유지합니다.`
      );
      return;
    }

    // 새로운 공격 애니메이션 설정
    this.attackAction = this.mixer.clipAction(finalClip);

    if (this.attackAction) {
      const duration = this.attackAction.getClip().duration;
      this.attackAction.setEffectiveTimeScale(duration / this.attackCooltime);
      this.attackAction.setLoop(THREE.LoopOnce);
      this.attackAction.clampWhenFinished = true;
      // 한국어 주석: 빈 트랙 문제 방지용으로 활성화 및 가중치 보정
      this.attackAction.enabled = true;
      this.attackAction.setEffectiveWeight(1.0);

      // allAnimations 배열 업데이트 (attackAction이 바뀌었으므로)
      this.allAnimations = [
        this.idleAction,
        this.moveAction,
        this.aimAction,
        this.attackAction,
        this.hitAction,
        this.deathAction,
        this.deathAction2,
      ].filter((action) => action !== null);
    }
  }

  // 애니메이션 인덱스 정의 (부모 클래스 메서드 오버라이드)
  getAnimationIndices() {
    return {
      idle: 36, // 대기 애니메이션
      move: 49, // 걷기 애니메이션
      aim: null, // 조준 애니메이션 없음
      attack: 68, // 공격 애니메이션
      hit: 34, // 피격 애니메이션
      death: 23, // 죽음 애니메이션 (뒤로 넘어지기)
    };
  }

  // 애니메이션 설정 - 부모 클래스 메서드 오버라이드
  setupAnimations(animations) {
    if (!animations || animations.length === 0) return;

    Utils.log(animations.map((animation) => animation.name));

    // 부모 클래스의 setupAnimations 호출 (공통 애니메이션 설정)
    super.setupAnimations(animations);

    // Player 전용: 추가 죽음 애니메이션 설정 (앞으로 넘어지기)
    if (animations[25]) {
      this.deathAction2 = this.mixer.clipAction(animations[25]);
      this.deathAction2.setLoop(THREE.LoopOnce);
      this.deathAction2.clampWhenFinished = true;

      const originalDuration2 = animations[25].duration;
      this.deathAction2.setEffectiveTimeScale(
        originalDuration2 / this.config.DEATH_ANIMATION_DURATION
      );
    }

    // allAnimations 배열에 추가 죽음 애니메이션 추가
    if (this.deathAction2) {
      this.allAnimations.push(this.deathAction2);
    }
  }

  // 플레이어 업데이트 - 부모 클래스 메서드 오버라이드
  update(delta, inputHandler, currentTime = Date.now() / 1000, camera = null) {
    if (!this.model) return;

    // 부모 클래스 업데이트 호출
    super.update(delta);

    // 부채꼴 시각화는 항상 업데이트 (플레이어가 살아있는 동안)
    if (this.isAlive) {
      this.updateAttackVisualization();
    }

    // 살아있을 때만 다른 로직 처리
    if (this.isAlive) {
      // 피격 상태가 아닐 때만 움직임과 전투 처리
      if (!this.isHit) {
        // 움직임 처리
        this.handleMovement(inputHandler);

        // 전투 시스템 업데이트
        this.updateCombat(currentTime);
      }

      // 체력바 업데이트
      this.updateHealthBar(camera);
    }
  }

  // 움직임 처리
  handleMovement(inputHandler) {
    let isMoving = false;
    const moveVector = new THREE.Vector3(0, 0, 0);

    // InputHandler로부터 이동 벡터 받기 (키보드 + 조이스틱 통합)
    const inputVector = inputHandler.getMovementVector(1);
    moveVector.copy(inputVector);

    // 이동 여부 확인
    isMoving = inputHandler.isMoving();

    // 애니메이션 전환
    this.handleAnimationTransition(isMoving);

    // 움직임 적용
    if (isMoving) {
      // 대각선 이동 시 속도 보정 (벡터 정규화)
      moveVector.normalize();
      moveVector.multiplyScalar(this.moveSpeed);

      // 새로운 위치 계산
      const newPosition = new THREE.Vector3()
        .copy(this.position)
        .add(moveVector);

      // 충돌 검사를 통한 안전한 이동
      this.updatePosition(newPosition);

      // 이동이 성공했을 때만 회전 처리
      this.handleRotation(moveVector);
    }

    this.isMoving = isMoving;
  }

  // 애니메이션 전환 처리 (이동 여부에 따라)
  handleAnimationTransition(isMoving) {
    // 공격 중이거나 피격 중일 때는 이동 애니메이션 전환 안함
    if (this.isAttacking || this.isHit) return;

    // 목표 애니메이션 결정
    const targetAction = isMoving ? this.moveAction : this.idleAction;

    // 현재 애니메이션이 목표 애니메이션과 같으면 전환 불필요
    if (this.currentAnimation === targetAction) return;

    // 안전한 애니메이션 전환 실행
    if (targetAction) {
      this.switchAnimation(this.currentAnimation, targetAction, 0.2);
    }
  }

  // 회전 처리
  handleRotation(moveVector) {
    if (moveVector.length() > 0) {
      const targetRotation = VectorUtils.lookAtDirectionImmediate(
        this.model.rotation.y,
        moveVector
      );

      // 현재 회전각과 목표 회전각 사이의 차이 계산
      let rotationDifference = targetRotation - this.model.rotation.y;

      // 회전 차이를 -π ~ π 범위로 정규화
      rotationDifference =
        MathUtils.normalizeRotationDifference(rotationDifference);

      // 부드러운 회전 적용
      this.model.rotation.y = MathUtils.smoothRotation(
        this.model.rotation.y,
        targetRotation,
        0.5
      );
    }
  }

  // attackEnemiesInSector는 Party에서 이미 공격 시각화를 처리하므로 오버라이드 불필요

  // 전투 업데이트 (매 프레임마다 호출)
  updateCombat(currentTime) {
    if (!this.model) return;

    // 부채꼴 범위 내에 적이 있으면 자동 공격 (애니메이션 상태와 무관하게 쿨타임만 체크)
    this.attackEnemiesInSector(currentTime);
  }

  // 공격 애니메이션 완료 시 호출되는 메서드 오버라이드
  onAttackAnimationFinished() {
    // 공격 범위 시각화 해제
    this.setAttackVisualHighlight(false);

    // 부모 클래스 메서드 호출
    super.onAttackAnimationFinished();
  }

  // 피격 처리 - 부모 클래스 메서드 오버라이드
  takeDamage(damage, attackerPosition = null) {
    if (super.takeDamage(damage, attackerPosition)) {
      this.handleDeath(attackerPosition);
      return true;
    }

    return false;
  }

  // 사망 처리 - 부모 클래스 메서드 오버라이드
  handleDeath(attackerPosition = null) {
    // 부모 클래스 메서드 호출 (공격 범위 시각화는 Party.die()에서 제거됨)
    super.die();

    // 모든 애니메이션 즉시 정지 (죽음 애니메이션 재생 전에 확실히 정리)
    this.stopAllAnimations();

    // 게임 종료 처리 호출 (공격 방향 정보 전달)
    // 죽음 애니메이션은 handleGameOver에서 재생됨
    if (window.game && window.game.handleGameOver) {
      window.game.handleGameOver(attackerPosition);
    }
  }

  // 죽음 애니메이션 재생 - 부모 클래스 메서드 오버라이드
  playDeathAnimation(attackerPosition = null) {
    // Player 전용: 공격 방향에 따라 다른 죽음 애니메이션 선택
    let selectedDeathAction;
    let deathType;

    if (attackerPosition && this.deathAction2) {
      // 플레이어와 공격자의 위치 벡터 계산
      const playerPos = this.getPosition();
      const attackDirection = new THREE.Vector3()
        .subVectors(playerPos, attackerPosition)
        .normalize();

      // 플레이어가 바라보는 방향 벡터 (rotation.y를 이용)
      const playerForward = VectorUtils.getPlayerForwardVector(
        this.model.rotation.y
      );

      // 공격 방향과 플레이어 전방의 내적으로 공격이 앞에서인지 뒤에서인지 판단
      const dot = attackDirection.dot(playerForward);

      if (dot < 0) {
        // 앞에서 공격받음 -> 뒤로 넘어지기 (deathAction)
        selectedDeathAction = this.deathAction;
        deathType = "뒤로 넘어지기";
      } else {
        // 뒤에서 공격받음 -> 앞으로 넘어지기 (deathAction2)
        selectedDeathAction = this.deathAction2;
        deathType = "앞으로 넘어지기";
      }

      Utils.log(`공격 방향 판단: ${deathType} (내적값: ${dot.toFixed(2)})`);
    } else {
      // 공격자 위치 정보가 없으면 기본 죽음 애니메이션 사용
      selectedDeathAction = this.deathAction;
      deathType = "뒤로 넘어지기 (기본)";
    }

    // 모든 애니메이션 즉시 정지
    this.stopAllAnimations();

    // 선택된 죽음 애니메이션을 실제로 재생
    if (selectedDeathAction) {
      selectedDeathAction.reset();
      selectedDeathAction.setEffectiveWeight(1.0);
      selectedDeathAction.play();
      this.currentAnimation = selectedDeathAction;

      Utils.log(`플레이어 죽음 애니메이션 재생: ${deathType}`);
    }
  }

  // 시체 상태 시작 (더 이상 사용하지 않음 - 죽음 애니메이션 마지막 프레임 유지로 대체)
  startCorpseState() {
    this.isDying = false;
    this.isCorpse = true;
  }

  // 포인트 정보 반환
  getPointsInfo() {
    return {
      current: this.points,
    };
  }

  // === 레벨 시스템 메서드들 ===

  // 다음 레벨까지 필요한 포인트 계산
  calculatePointsToNextLevel() {
    const basePoints = GameConfig.LEVEL_SYSTEM.EXP_PER_LEVEL;
    const scaling = GameConfig.LEVEL_SYSTEM.EXP_SCALING;
    return Math.floor(basePoints * Math.pow(scaling, this.level - 1));
  }

  // 포인트 추가 (경험치 역할)
  addPoints(points) {
    if (!this.isAlive) return; // 죽은 플레이어는 포인트 획득 불가

    this.points += points;

    // 레벨업 체크
    this.checkLevelUp();

    // 한국어 주석: 업그레이드 창이 열려있을 때 경험치를 쌓았다면, 나중에 다시 체크하기 위한 플래그 설정
    if (
      window.game &&
      window.game.uiManager &&
      window.game.uiManager.isUpgradeSelectionOpen &&
      this.points >= this.pointsToNextLevel &&
      this.level < GameConfig.LEVEL_SYSTEM.MAX_LEVEL
    ) {
      this.needsLevelUpRecheck = true; // 레벨업 재체크 필요 플래그
    }
  }

  // 레벨업 체크 및 처리 (레벨바가 끝에 닿을 때까지 대기)
  checkLevelUp() {
    // 업그레이드 창이 열려있으면 레벨업 체크 안함 (중복 방지)
    if (
      window.game &&
      window.game.uiManager &&
      window.game.uiManager.isUpgradeSelectionOpen
    ) {
      return;
    }

    // 레벨업 조건을 만족하면 레벨업 대기 상태로 설정 (바로 레벨업하지 않음)
    if (
      this.points >= this.pointsToNextLevel &&
      this.level < GameConfig.LEVEL_SYSTEM.MAX_LEVEL &&
      !this.isLevelUpPending
    ) {
      this.isLevelUpPending = true; // 레벨업 대기 상태 설정
    }
  }

  // 실제 레벨업 처리 (레벨바가 끝에 닿았을 때 호출)
  processLevelUp() {
    if (!this.isLevelUpPending) return; // 대기 상태가 아니면 무시

    // 포인트 차감 및 레벨 증가
    this.points -= this.pointsToNextLevel;
    this.level++;

    // 다음 레벨까지 필요한 포인트 재계산
    this.pointsToNextLevel = this.calculatePointsToNextLevel();

    // 레벨업 대기 상태 해제
    this.isLevelUpPending = false;

    // 레벨업 처리
    this.onLevelUp();
  }

  // TODO : 레벨업 시 회복되는 체력의 %도 Config 파일에서 해결하도록
  // 레벨업 시 호출되는 메서드 (업그레이드 시스템)
  onLevelUp() {
    // 레벨업 시 체력 회복 (20% 회복)
    const healAmount = Math.floor(this.maxHealth * 0.2);
    this.heal(healAmount);

    // 업그레이드 매니저를 통해 업그레이드 선택지 표시
    if (window.game && window.game.upgradeManager) {
      window.game.upgradeManager.showUpgradeOptions();
    }
  }

  // 해골 처치 카운트 증가
  addKill(enemyType) {
    if (!this.isAlive) return; // 죽은 플레이어는 킬 카운트 증가 불가

    this.killedSkeletons++;
  }

  // 레벨 정보 반환
  getLevelInfo() {
    return {
      level: this.level,
      points: this.points,
      pointsToNextLevel: this.pointsToNextLevel,
      progressPercentage: (this.points / this.pointsToNextLevel) * 100,
      killedSkeletons: this.killedSkeletons,
      isLevelUpPending: this.isLevelUpPending, // 레벨업 대기 상태 정보 추가
    };
  }

  // 업그레이드 창이 닫힌 후 레벨업 재체크
  recheckLevelUp() {
    if (this.needsLevelUpRecheck) {
      this.needsLevelUpRecheck = false;
      this.checkLevelUp();
    }
  }
}

// webpack 빌드를 위해 default export 추가
export default Player;

// 전역 변수로도 등록 (기존 코드 호환성)
window.Player = Player;
